import { Injectable } from "@angular/core";
import { IEvent, MatchStatus } from "./match.service";
import centroid from "@turf/centroid";
import { ISelectedPlayer } from "../map/players-list/players-list.component";

@Injectable()
export class StatsService {
  constructor() {}

  filterEvents(
    events: IEvent[],
    quarter = MatchStatus.Finished,
    players: number[] = []
  ) {
    let zoneEvents;
    const isFinished = [
      MatchStatus.Cancelled,
      MatchStatus.Postponed,
      MatchStatus.Suspended,
    ].includes(quarter);
    if (quarter === MatchStatus.Finished) {
      // 0 is for full match
      zoneEvents = events;
    } else if (!isFinished) {
      zoneEvents = events.filter((elem) => elem.quarter === quarter);
    } else {
      return [];
    }

    if (players.length) {
      zoneEvents = zoneEvents.filter(
        (elem) => players.indexOf(elem.player_0_id) !== -1
      );
    }
    return zoneEvents;
  }

  calculateZoneStats(
    events: IEvent[],
    teams: any,
    quarter = 0,
    players: number[] = []
  ) {
    const zoneEvents = this.filterEvents(events, quarter, players);

    const homeTeam = teams.home.id;
    const visitorTeam = teams.visitor.id;

    const zoneStats = zoneEvents.reduce((acum, elem) => {
      if (!elem.zone_id) {
        if (elem.team_0 === homeTeam) {
          elem.zone_id = 21;
        } else {
          elem.zone_id = 22;
        }
      }

      if (!acum[elem.zone_id]) {
        acum[elem.zone_id] = {
          points: 0,
          shots: 0,
          failures: 0,
        };
      }
      acum[elem.zone_id].points += elem.points;
      acum[elem.zone_id].shots += elem.shots;
      acum[elem.zone_id].failures += elem.failures ? 1 : 0;

      return acum;
    }, {});

    return zoneStats;
  }

  calculateZoneLeader(
    events: IEvent[],
    teams: any,
    quarter = 0,
    players: number[] = []
  ) {
    const zoneEvents = this.filterEvents(events, quarter, players);

    const homeTeam = teams.home.id;
    const visitorTeam = teams.visitor.id;

    const zoneStats = zoneEvents.reduce((acum, elem) => {
      if (!elem.zone_id) {
        if (elem.team_0 === homeTeam) {
          elem.zone_id = 21;
        } else {
          elem.zone_id = 22;
        }
      }

      if (!acum[elem.zone_id]) {
        acum[elem.zone_id] = {
          points: 0,
          shots: 0,
          failures: 0,
          leader: {},
        };
      }

      acum[elem.zone_id].leader[elem.player_0_id] = acum[elem.zone_id].leader[
        elem.player_0_id
      ] || { points: 0, shots: 0, failures: 0, id: elem.player_0_id };
      acum[elem.zone_id].leader[elem.player_0_id].points += elem.points;
      acum[elem.zone_id].leader[elem.player_0_id].shots += elem.shots;
      acum[elem.zone_id].leader[elem.player_0_id].failures += elem.failures
        ? 1
        : 0;

      return acum;
    }, {});

    for (let i = 1; i <= 22; i++) {
      if (zoneStats[i]) {
        const stats = Object.values(zoneStats[i].leader);
        const leader: any = stats.reduce(
          (prev: any, current: any) =>
            prev.points > current.points ? prev : current,
          -1
        );
        zoneStats[i] = {
          points: leader.points,
          shots: leader.shots,
          failures: leader.failures,
          leader: leader.id,
        };
      }
    }

    return zoneStats;
  }

  calculateAccuracy(zoneStats) {
    let accuracy;
    if (!zoneStats.failures && zoneStats.shots) {
      accuracy = 1;
    } else if (!zoneStats.shots) {
      accuracy = 0;
    } else {
      accuracy = 1 - zoneStats.failures / zoneStats.shots;
    }
    return Math.round(accuracy * 100);
  }

  calculateResult(
    events: IEvent[],
    teams: any,
    quarter: MatchStatus = MatchStatus.Finished,
    cumulative = true
  ) {
    let zoneEvents;
    let result = {
      home: 0,
      visitor: 0,
    };

    const isFinished = [
      MatchStatus.Cancelled,
      MatchStatus.Postponed,
      MatchStatus.Suspended,
    ].includes(quarter);

    if (quarter === MatchStatus.Finished) {
      zoneEvents = events;
    } else if (!isFinished) {
      zoneEvents = events.filter((elem, idx) => {
        if (cumulative) {
          return (
            elem.quarter <= quarter && elem.quarter !== MatchStatus.Finished
          );
        } else {
          return elem.quarter === quarter;
        }
      });
    } else {
      return result;
    }

    if (zoneEvents.length) {
      const firstEvent = zoneEvents[0];
      const lastEvent = zoneEvents[zoneEvents.length - 1];
      result = {
        home: cumulative
          ? lastEvent.score_home
          : lastEvent.score_home - firstEvent.score_home,
        visitor: cumulative
          ? lastEvent.score_visitor
          : lastEvent.score_visitor - firstEvent.score_visitor,
      };
    } else {
      return result;
    }

    return result;
  }

  calculateGrid(
    events: IEvent[],
    teams,
    quarter = 0,
    players: ISelectedPlayer[] = []
  ) {
    const playersIds = players.map((p) => p.id);
    const zoneEvents = this.filterEvents(events, quarter, playersIds);
    return zoneEvents
      .filter((ev) => ev.shots > 0)
      .reduce((prev, next) => {
        const isHomeTeam = next.team_0 === teams.home.id;
        if (!next.grid_id) {
          next.grid_id = isHomeTeam ? 202 : 219;
        }
        prev[next.grid_id] = prev[next.grid_id] || {
          shots: 0,
          points: 0,
          failures: 0,
        };
        prev[next.grid_id].team = isHomeTeam ? "home" : "visitor";
        prev[next.grid_id].shots += next.shots;
        prev[next.grid_id].points += next.points;
        prev[next.grid_id].failures += next.failures ? 1 : 0;
        return prev;
      }, {});
  }

  calculateGridCenters(geojson, zoneStats) {
    return {
      type: "FeatureCollection",
      features: geojson.features.map((feat) => {
        return {
          type: feat.type,
          geometry: centroid(feat.geometry).geometry,
          properties: {
            id: feat.properties.id,
            grid_data: zoneStats[feat.properties.id],
          },
        };
      }),
    };
  }

  getAccuracyColor(stats) {
    const step = 100 / 5;
    const value = stats ? this.calculateAccuracy(stats) : 0;

    if (value === 0) {
      return "#FFF8E9";
    } else if (value < step) {
      return "#FEEDC8";
    } else if (value < step * 2) {
      return "#8fa0f54d";
    } else if (value < step * 3) {
      return "#8fa0f5cc";
    } else if (value < step * 4) {
      return "#8760F6";
    } else {
      return "#3F18B0";
    }
  }
}
